import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { updateGeneralSettings } from '../store/actions/SettingsActions';

type ThemeType = 'light' | 'dark' | 'system';

interface ThemeContextProps {
  theme: ThemeType;
  isThemeLoading: boolean;
  setTheme: (theme: ThemeType) => void;
  setThemeSelection: (theme: ThemeType) => Promise<void>;
  toggleTheme: () => Promise<void>;
}

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeContext = createContext<ThemeContextProps>({
  theme: 'light',
  isThemeLoading: false,
  setTheme: () => {},
  setThemeSelection: async () => {},
  toggleTheme: async () => {},
});

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const savedTheme = localStorage.getItem('theme') as ThemeType | null;
  const [theme, setThemeState] = useState<ThemeType>(savedTheme || 'light');
  const [isThemeLoading, setIsThemeLoading] = useState<boolean>(false);
  const generalSettings = useSelector((state: RootState) => state.user?.userInfo?.preferences?.general_settings);
  const dispatch = useAppDispatch();

  // Sync theme from preferences to local state when preferences change
  useEffect(() => {
    if (generalSettings?.theme && generalSettings.theme !== theme) {
      const newTheme = generalSettings.theme as ThemeType;
      setThemeState(newTheme);
      applyTheme(newTheme);
      localStorage.setItem('theme', newTheme);
    }
  }, [generalSettings?.theme, theme]);

  // Follow browser/OS color scheme when 'system' is selected
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => applyTheme('system');
    handleChange(); // apply immediately

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const applyTheme = (newTheme: ThemeType) => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    let activeTheme: 'light' | 'dark';

    if (newTheme === 'system') {
      activeTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } else {
      activeTheme = newTheme;
    }

    root.classList.add(activeTheme);
  };

  const setTheme = (newTheme: ThemeType) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const setThemeSelection = async (newTheme: ThemeType) => {
    setIsThemeLoading(true);
    try {
      await dispatch(
        updateGeneralSettings({
          theme: newTheme,
          language: generalSettings?.language || 'en',
          currency: generalSettings?.currency || 'EUR',
        }) as any
      );
      setTheme(newTheme);
    } catch (error) {
      console.error('Failed to update theme:', error);
    } finally {
      setIsThemeLoading(false);
    }
  };

  /**
   * Toggle theme between light and dark mode
   * Uses the new set-preferences API to persist theme changes
   * Other general settings are automatically preserved by updateGeneralSettings
   */
  const toggleTheme = async () => {
    setIsThemeLoading(true);
    const themeMap: Record<ThemeType, ThemeType> = {
      light: 'dark',
      dark: 'light',
      system: 'light',
    };
    const newTheme = themeMap[theme];

    try {
      await dispatch(
        updateGeneralSettings({
          theme: newTheme,
          language: generalSettings?.language || 'en',
          currency: generalSettings?.currency || 'EUR',
        }) as any
      );
      setTheme(newTheme);
    } catch (error) {
      setTheme(theme);
    } finally {
      setIsThemeLoading(false);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, isThemeLoading, setTheme, setThemeSelection, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
